import React from "react";
import Loader from "../loader/loader.ui";
import EmptyState from "../empty_state/empty_state.ui";
import "./data_table.ui.scss";

export interface IDataTableColumn {
  key: string;
  label: string;
  sortable?: boolean;
  align?: "right";
  // Optional column width (e.g. "34%") so list tables keep stable column proportions.
  width?: string;
  render?: (row: any) => React.ReactNode;
}

interface IDataTable {
  columns: IDataTableColumn[];
  rows: any[];
  rowKey: string;
  loading?: boolean;
  emptyMessage?: string;
  onSort?: (key: string, direction: "asc" | "desc") => void;
  onRowClick?: (row: any) => void;
}

const DataTable = (props: IDataTable) => {
  const { columns, rows, rowKey, loading, emptyMessage, onSort, onRowClick } = props;
  const [sortState, setSortState] = React.useState<{ key: string; direction: "asc" | "desc" } | null>(null);

  const handleSort = (column: IDataTableColumn) => {
    if (!column.sortable || !onSort) {
      return;
    }
    const nextDirection: "asc" | "desc" =
      sortState?.key === column.key && sortState.direction === "asc" ? "desc" : "asc";
    setSortState({ key: column.key, direction: nextDirection });
    onSort(column.key, nextDirection);
  };

  if (loading) {
    return (
      <div className="data_table_loading" data-testid="data_table_loading">
        <Loader width="32px" height="32px" />
      </div>
    );
  }

  if (!rows.length) {
    return <EmptyState message={emptyMessage || "No data to display"} />;
  }

  return (
    <table className={`data_table${columns.some((column) => column.width) ? " data_table_fixed" : ""}`}>
      <thead>
        <tr>
          {columns.map((column) => (
            <th
              key={column.key}
              className={`${column.sortable ? "data_table_header_sortable" : ""}${column.align === "right" ? " data_table_cell_right" : ""}`.trim()}
              style={column.width ? { width: column.width } : undefined}
              onClick={() => handleSort(column)}
            >
              {column.label}
              {sortState?.key === column.key && (sortState.direction === "asc" ? " ▲" : " ▼")}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr
            key={row[rowKey]}
            className={onRowClick ? "data_table_row_clickable" : ""}
            onClick={() => onRowClick && onRowClick(row)}
          >
            {columns.map((column) => (
              <td key={column.key} className={column.align === "right" ? "data_table_cell_right" : ""}>{column.render ? column.render(row) : row[column.key]}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default DataTable;
